<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { PnlReportReadModel } from "@Quest/Pnls/Domain/readmodels";


    @Component({
        props: {
            readModel: {
                type: Object,
                required: true
            },
            cagrVisible:
            {
                type: Boolean,
                required: false,
                default: false
            }
        }
    })
    export default class PnlViewer extends Vue
    {
        get rm(): PnlReportReadModel { return this.$props.readModel as PnlReportReadModel; }
        get numberOfColumns(): number { return this.rm.Columns.length; }

        revenuesCollapsed: boolean = true;
        opexCollapsed: boolean = true;
        cogsCollapsed: boolean = true;
        otherExpensesCollapsed: boolean = true;

        toggleRevenues() { this.revenuesCollapsed = !this.revenuesCollapsed; }
        toggleOpex() { this.opexCollapsed = !this.opexCollapsed; }
        toggleCogs() { this.cogsCollapsed = !this.cogsCollapsed; }
        toggleOtherExpenses() { this.otherExpensesCollapsed = !this.otherExpensesCollapsed; }
    }
</script>

<template>
    <div v-if="rm">
        <table class="table table-bordered">
            <thead class="table-secondary" style="border-collapse: collapse; border-left: 5px solid gray;">
                <tr>
                    <th>All values in: {{rm.CurrencyLabel}} </th>
                    <th v-for="c in rm.Columns">{{c.Label}}</th>
                    <th class="table-info" v-if="cagrVisible">3yr CAGR</th>
                    <th class="table-info" v-if="cagrVisible">5yr CAGR</th>
                </tr>
            </thead>

            <tbody class="revenues">
                <tr>
                    <th>
                        <div @click="toggleRevenues" class="pointer">
                            <div style="display: flex; align-items: center; flex-grow: 1;">
                                <i :class="['fa', revenuesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                {{rm.Revenues.Label}}
                            </div>
                            <div class="text-muted">% growth</div>
                        </div>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.Revenues.Values[i-1]}}
                        <br />
                        <small class="text-muted">{{rm.Revenues.GrowthPercentages[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Revenues.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Revenues.Cagr5yr}}
                    </th>
                </tr>
                <tr v-for="r in rm.Revenues.Accounts" :key="r.Id" v-if="!revenuesCollapsed">
                    <td>
                        {{r.Label}}<br />
                        <span class="text-muted">% growth</span>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{r.Values[i-1]}}<br />
                        <small class="text-muted">{{r.GrowthPercentages[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{r.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{r.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="cogs">
                <tr>
                    <th>
                        <div @click="toggleCogs" class="pointer" style="display: flex; align-items: center; flex-grow: 1;">
                            <i :class="['fa', cogsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                            {{rm.Cogs.Label}}
                        </div>
                    </th>

                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.Cogs.Values[i-1]}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Cogs.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Cogs.Cagr5yr}}
                    </th>
                </tr>
                <tr v-if="!cogsCollapsed" v-for="c in rm.Cogs.Accounts" :key="c.Id">
                    <td>{{c.Label}}</td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{c.Values[i-1]}}
                    </td>
                    <td v-if="cagrVisible">
                        {{c.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{c.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="cogs" v-if="!cogsCollapsed" v-for="sub in rm.Cogs.Subtotals" :key="sub.Id">
                <tr>
                    <td>
                        <div @click="sub.IsCollapsed = !sub.IsCollapsed" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', sub.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                {{sub.Label}}
                            </div>
                        </div>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{sub.Values[i-1]}}<br />
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr5yr}}
                    </td>
                </tr>
                <tr v-for="e in sub.Accounts" :key="e.Id" v-if="!sub.IsCollapsed">
                    <td>
                        {{e.Label}}
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="gross-margin">
                <tr>
                    <th>
                        Gross margin <br />
                        <span class="text-muted">% of sales</span>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.GrossMargin.Values[i-1]}}<br />
                        <small class="text-muted">{{rm.GrossMargin.PercentageOfSales[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.GrossMargin.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.GrossMargin.Cagr5yr}}
                    </th>
                </tr>
            </tbody>

            <tbody class="opex">
                <tr>
                    <th>
                        <div @click="toggleOpex" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', opexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                Opex
                            </div>
                            <div class="text-muted">% of sales</div>
                        </div>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.Opex.Values[i-1]}}<br />
                        <small class="text-muted">{{rm.Opex.PercentageOfSales[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Opex.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.Opex.Cagr5yr}}
                    </th>
                </tr>
                <tr v-if="!opexCollapsed" v-for="e in rm.Opex.Accounts" :key="e.Id">
                    <td>
                        {{e.Label}}<br />
                        <span class="text-muted">% of sales</span>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}<br />
                        <small class="text-muted">{{e.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="opex" v-if="!opexCollapsed" v-for="sub in rm.Opex.Subtotals" :key="sub.Id">
                <tr>
                    <td>
                        <div @click="sub.IsCollapsed = !sub.IsCollapsed" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', sub.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                {{sub.Label}}
                            </div>
                            <div class="text-muted">% of sales</div>
                        </div>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{sub.Values[i-1]}}<br />
                        <small class="text-muted">{{sub.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr5yr}}
                    </td>
                </tr>
                <tr v-for="e in sub.Accounts" :key="e.Id" v-if="!sub.IsCollapsed">
                    <td>
                        {{e.Label}}<br />
                        <span class="text-muted">% of sales</span>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}<br />
                        <small class="text-muted">{{e.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="ebita">
                <tr>
                    <th>
                        EBITA <br />
                        <span class="text-muted">% of sales</span>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.EBITA.Values[i-1]}}<br />
                        <small class="text-muted">{{rm.EBITA.PercentageOfSales[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.EBITA.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.EBITA.Cagr5yr}}
                    </th>
                </tr>
            </tbody>

            <tbody class="other-expenses">
                <tr>
                    <th>
                        <div @click="toggleOtherExpenses" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', otherExpensesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                Other expenses
                            </div>
                            <div class="text-muted">% of sales</div>
                        </div>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.OtherExpenses.Values[i-1]}}<br />
                        <small class="text-muted">{{rm.OtherExpenses.PercentageOfSales[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.OtherExpenses.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.OtherExpenses.Cagr5yr}}
                    </th>
                </tr>
                <tr v-if="!otherExpensesCollapsed" v-for="e in rm.OtherExpenses.Accounts" :key="e.Id">
                    <td>
                        {{e.Label}}<br />
                        <span class="text-muted">% of sales</span>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}<br />
                        <small class="text-muted">{{e.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="other-expenses" v-if="!otherExpensesCollapsed" v-for="sub in rm.OtherExpenses.Subtotals" :key="sub.Id">
                <tr>
                    <td>
                        <div @click="sub.IsCollapsed = !sub.IsCollapsed" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', sub.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                {{sub.Label}}
                            </div>
                            <div class="text-muted">% of sales</div>
                        </div>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{sub.Values[i-1]}}<br />
                        <small class="text-muted">{{sub.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr5yr}}
                    </td>
                </tr>
                <tr v-for="e in sub.Accounts" :key="e.Id" v-if="!sub.IsCollapsed">
                    <td>
                        {{e.Label}}<br />
                        <span class="text-muted">% of sales</span>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}<br />
                        <small class="text-muted">{{e.PercentageOfSales[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

            <tbody class="adjusted-ebita">
                <tr>
                    <th>
                        Adjusted EBITA <br />
                        <span class="text-muted">% of sales</span>
                    </th>
                    <th v-for="i in numberOfColumns" :key="i">
                        {{rm.AdjustedEBITA.Values[i-1]}}<br />
                        <small class="text-muted">{{rm.AdjustedEBITA.PercentageOfSales[i-1]}}</small>
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.AdjustedEBITA.Cagr3yr}}
                    </th>
                    <th v-if="cagrVisible">
                        {{rm.AdjustedEBITA.Cagr5yr}}
                    </th>
                </tr>
            </tbody>

            <tbody class="spacer-row">
                <tr>
                    <th></th>
                    <th v-for="c in rm.Columns"></th>
                    <th class="table-info" v-if="cagrVisible"></th>
                    <th class="table-info" v-if="cagrVisible"></th>
                </tr>
            </tbody>
            
            <thead class="table-secondary" style="border-collapse: collapse; border-left: 5px solid gray;">
                <tr>
                    <th>Resources</th>
                    <th v-for="c in rm.Columns">{{c.Label}}</th>
                    <th class="table-info" v-if="cagrVisible">3yr CAGR</th>
                    <th class="table-info" v-if="cagrVisible">5yr CAGR</th>
                </tr>
            </thead>

            <tbody class="resources" v-for="sub in rm.Resources.Subtotals" :key="sub.Unit">
                <tr>
                    <td>
                        <div @click="sub.IsCollapsed = !sub.IsCollapsed" class="pointer">
                            <div style="display: flex; align-items: center;">
                                <i :class="['fa', sub.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" style="margin-right: 10px;"></i>
                                {{sub.Label}}
                            </div>
                            <div class="text-muted">% growth</div>
                        </div>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{sub.Values[i-1]}}<br />
                        <small class="text-muted">{{sub.GrowthPercentages[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{sub.Cagr3yr}}
                    </td>
                </tr>
                <tr v-for="e in sub.Accounts" :key="e.Id" v-if="!sub.IsCollapsed">
                    <td>
                        {{e.Label}}<br />
                        <div class="text-muted">% growth</div>
                    </td>
                    <td v-for="i in numberOfColumns" :key="i">
                        {{e.Values[i-1]}}<br />
                        <small class="text-muted">{{e.GrowthPercentages[i-1]}}</small>
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr3yr}}
                    </td>
                    <td v-if="cagrVisible">
                        {{e.Cagr5yr}}
                    </td>
                </tr>
            </tbody>

        </table>
    </div>
</template>      


<style scoped>

    .spacer-row th {
        height: 20px; /* Adjust height as needed */
        background-color: transparent !important; /* Ensure it blends in */
        border: none !important; /* Remove any default borders */
        padding: 0; /* Remove padding to maintain a clean look */
    }

    /* Base style to ensure no borders are doubled up */
    tbody tr {
        border-collapse: collapse;
        border-left: 5px solid transparent; /* Base transparent border */
    }

    .revenues tr:first-child {
        border-top-left-radius: 10px; /* Optional: rounded corners */
    }

    .revenues tr:last-child {
        border-bottom-left-radius: 10px; /* Optional: rounded corners */
    }

    .revenues tr {
        border-left-color: #A8E6CF; /* Light green for Revenues */
    }

    .cogs tr:first-child {
        border-top-left-radius: 10px; /* Optional: rounded corners */
    }

    .cogs tr:last-child {
        border-bottom-left-radius: 10px; /* Optional: rounded corners */
    }

    .cogs tr {
        border-left-color: #D8D8D8; /* Light grey for Cogs */
    }

    .opex tr:first-child {
        border-top-left-radius: 10px; /* Optional: rounded corners */
    }

    .opex tr:last-child {
        border-bottom-left-radius: 10px; /* Optional: rounded corners */
    }

    .opex tr {
        border-left-color: #FFD97D; /* Light orange for Opex */
    }

    .gross-margin tr:first-child {
        border-top-left-radius: 10px; /* Optional: rounded corners */
    }

    .gross-margin tr:last-child {
        border-bottom-left-radius: 10px; /* Optional: rounded corners */
    }

    .gross-margin tr {
        border-left-color: #A2D5F2; /* Light blue for Gross Margin */
    }

    .ebita tr:first-child {
        border-top-left-radius: 10px; /* Optional: rounded corners */
    }

    .ebita tr:last-child {
        border-bottom-left-radius: 10px; /* Optional: rounded corners */
    }

    .ebita tr {
        border-left-color: #56C596; /* Darker green for Ebita */
    }


    .other-expenses tr:first-child {
        border-top-left-radius: 10px; /* Rounded corners */
    }

    .other-expenses tr:last-child {
        border-bottom-left-radius: 10px; /* Rounded corners */
    }

    .other-expenses tr {
        border-left-color: #FF6B6B; /* Light red for Other Expenses */
    }

    .adjusted-ebita tr:first-child {
        border-top-left-radius: 10px; /* Rounded corners */
    }

    .adjusted-ebita tr:last-child {
        border-bottom-left-radius: 10px; /* Rounded corners */
    }

    .adjusted-ebita tr {
        border-left-color: #4D96FF; /* Light blue for Adjusted EBITA */
    }

    /* ---- Resource Utilization Section ---- */
    .resources tr:first-child {
        border-top-left-radius: 10px; /* Rounded corners */
    }

    .resources tr:last-child {
        border-bottom-left-radius: 10px; /* Rounded corners */
    }

    .resources tr {
        border-left-color: #FDCB58; /* Light Yellow-Orange for Resource Utilization */
    }

</style>